import { useState, type ReactNode } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/features/auth';
import { ErrorState } from '@/shared/components/error-state';

import { useFinancialReport } from '../api/reports.queries';
import {
  FINANCIAL_VIEWS,
  FINANCIAL_VIEW_LABEL,
  PARTNER_FINANCIAL_VIEWS,
  readFinancialView,
  usesFinancialReport,
  type FinancialView,
} from '../lib/financial-views';
import { CLEAR_REPORT_FILTERS, type ReportFilterChanges } from '../lib/report-filters';
import { isTimeGrouping } from '../lib/report-labels';
import type { FinancialReport, ReportParams } from '../types/report.types';
import { FinancialChannelsDaily } from './financial-channels-daily';
import { FinancialCharts } from './financial-charts';
import { FinancialIssues, type FinancialIssueLinks } from './financial-issues';
import { FinancialPartnerShare, PartnerShareTotals } from './financial-partner-share';
import {
  FinancialRefunds,
  FinancialSettlement,
  FinancialWaterfallCards,
} from './financial-summary';
import { FinancialTable } from './financial-table';
import { FinancialTransactions } from './financial-transactions';
import { ReportNoData } from './report-no-data';

/**
 * The Financial tab, split into sub-tabs (the open one is `fin` in the URL):
 * Summary (the waterfall per currency, trends and the money table), Payment
 * channels by day, Partner share, Transactions, Settlement and Issues. Every
 * sub-tab uses the shared filters; only the open one is fetched and printed.
 *
 * The Partner (`partnerOnly`: reports.partner.read without reports.financial.read)
 * sees only Summary — the totals of what was collected, its share and its net
 * share — and Transactions without cost.
 */
export function FinancialTab({
  partnerOnly = false,
  view,
  onViewChange,
  params,
  scopeAll,
  filtered,
  onFilter,
}: {
  partnerOnly?: boolean;
  view: FinancialView;
  onViewChange: (view: FinancialView) => void;
  params: ReportParams;
  /** The report covers all of the staff member's countries. */
  scopeAll: boolean;
  /** A channel, provider, brand or denomination filter is set. */
  filtered: boolean;
  /** Narrows the whole report (a brand, provider, channel or denomination clicked in a row). */
  onFilter: (changes: ReportFilterChanges) => void;
}) {
  const financial = useFinancialReport(params, !partnerOnly && usesFinancialReport(view));
  const clearFilters = () => onFilter(CLEAR_REPORT_FILTERS);
  const views = partnerOnly ? PARTNER_FINANCIAL_VIEWS : FINANCIAL_VIEWS;

  const fromFinancialReport = (render: (report: FinancialReport) => ReactNode) => {
    if (financial.isLoading) return <Skeleton className="h-64" />;
    if (financial.error)
      return <ErrorState error={financial.error} onRetry={() => void financial.refetch()} />;
    if (
      !financial.data ||
      (financial.data.rows.length === 0 && financial.data.refunds.length === 0)
    )
      return <ReportNoData filtered={filtered} onClear={clearFilters} />;
    return render(financial.data);
  };

  return (
    <Tabs value={view} onValueChange={(value) => onViewChange(readFinancialView(value))}>
      <div className="overflow-x-auto" data-print="hide">
        <TabsList variant="line" aria-label="Financial reports">
          {views.map((item) => (
            <TabsTrigger key={item} value={item} className="text-xs">
              {FINANCIAL_VIEW_LABEL[item]}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      <TabsContent value="summary" className="pt-3">
        {view === 'summary' && partnerOnly && (
          <PartnerShareTotals params={params} filtered={filtered} onClearFilters={clearFilters} />
        )}
        {view === 'summary' &&
          !partnerOnly &&
          fromFinancialReport((report) => (
            <FinancialSummaryView report={report} params={params} onFilter={onFilter} />
          ))}
      </TabsContent>

      <TabsContent value="channels" className="pt-3">
        {view === 'channels' && (
          <FinancialChannelsDaily
            params={params}
            filtered={filtered}
            onClearFilters={clearFilters}
          />
        )}
      </TabsContent>

      <TabsContent value="partners" className="pt-3">
        {view === 'partners' && (
          <FinancialPartnerShare
            params={params}
            filtered={filtered}
            onClearFilters={clearFilters}
          />
        )}
      </TabsContent>

      <TabsContent value="transactions" className="pt-3">
        {view === 'transactions' && <FinancialTransactions params={params} onFilter={onFilter} />}
      </TabsContent>

      <TabsContent value="settlement" className="pt-3">
        {view === 'settlement' &&
          fromFinancialReport((report) => <FinancialSettlement report={report} />)}
      </TabsContent>

      <TabsContent value="issues" className="pt-3">
        {view === 'issues' &&
          fromFinancialReport((report) => (
            <FinancialIssuesView report={report} params={params} scopeAll={scopeAll} />
          ))}
      </TabsContent>
    </Tabs>
  );
}

/** The currencies a report has money in; charts show one at a time. */
function useChartCurrency(report: FinancialReport) {
  const currencies = report.totals.flatMap((total) =>
    total.currencyCode ? [total.currencyCode] : [],
  );
  const [chosen, setChosen] = useState<string | null>(null);
  const currency = chosen && currencies.includes(chosen) ? chosen : (currencies[0] ?? null);
  const picker =
    currencies.length > 1 && currency ? (
      <div className="flex justify-end" data-print="hide">
        <Select value={currency} onValueChange={setChosen}>
          <SelectTrigger size="sm" className="h-8 w-32 text-xs" aria-label="Chart currency">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {currencies.map((code) => (
              <SelectItem key={code} value={code} className="text-xs">
                Charts in {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;
  return { currency, picker };
}

function FinancialSummaryView({
  report,
  params,
  onFilter,
}: {
  report: FinancialReport;
  params: ReportParams;
  onFilter: (changes: ReportFilterChanges) => void;
}) {
  const { currency, picker } = useChartCurrency(report);
  // Trends need a time axis: when the report is grouped by something else,
  // the same filters are fetched again by day for the charts.
  const timeGrouped = isTimeGrouping(params.groupBy);
  const byDay = useFinancialReport({ ...params, groupBy: 'day' }, !timeGrouped);
  const trend = timeGrouped ? report : byDay.data;

  return (
    <div className="space-y-4">
      <FinancialWaterfallCards totals={report.totals} />
      {picker}
      {currency && (
        <FinancialCharts
          report={report}
          trend={trend}
          trendLoading={!timeGrouped && byDay.isLoading}
          trendError={timeGrouped ? null : byDay.error}
          onRetryTrend={() => void byDay.refetch()}
          currency={currency}
        />
      )}
      {report.rows.length > 0 && <FinancialTable report={report} onFilter={onFilter} />}
      <FinancialRefunds report={report} />
    </div>
  );
}

function FinancialIssuesView({
  report,
  params,
  scopeAll,
}: {
  report: FinancialReport;
  params: ReportParams;
  scopeAll: boolean;
}) {
  const { can } = usePermissions();
  const { currency, picker } = useChartCurrency(report);

  const dates = `from=${params.from}&to=${params.to}`;
  const links: FinancialIssueLinks = {
    pricing: can('reports.pricing.read')
      ? `/reports?tab=pricing&${dates}${scopeAll ? '&scope=all' : ''}`
      : undefined,
    refunds: can('refunds.request') ? '/refunds?tab=recorded' : undefined,
    reconciliation: can('reconciliation.read')
      ? `/reconciliation?check=failed_after_payment&${dates}`
      : undefined,
    settlements: can('settlements.read') ? '/settlements' : undefined,
  };

  if (!currency) return null;
  return (
    <div className="space-y-4">
      {picker}
      <FinancialIssues params={params} currency={currency} links={links} />
    </div>
  );
}
